import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Users, Calendar, Trophy, Heart, Star, Target, MapPin, TrendingUp, MessageCircle } from 'lucide-react';
import { userAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const FanDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getMyProfile();
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const stats = profile?.stats || {};

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="fan-dashboard">
      {/* Header */}
      <motion.div 
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Fan Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Your sports community hub
            </p>
          </div>
        </div>
      </motion.div>

      {/* Profile Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5"
      >
        <Card className="card-premium overflow-hidden border-0">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16 ring-2 ring-primary/20 ring-offset-2 ring-offset-background flex-shrink-0">
                <AvatarImage src={currentUser.profileImage} alt={currentUser.name} />
                <AvatarFallback className="text-lg font-bold bg-primary text-primary-foreground">
                  {currentUser.name?.[0] || 'F'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-bold text-foreground truncate">{currentUser.name}</h2>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                        <Heart className="w-3 h-3 mr-1.5 text-primary" />
                        {currentUser.sports?.[0] || 'Sports Fan'}
                      </span>
                      <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                        <MapPin className="w-3 h-3 mr-1.5" />
                        {currentUser.location || 'Not specified'}
                      </span>
                    </div>
                  </div>
                  <Badge className="badge-primary text-xs flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    Premium
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Stats Grid */}
      <motion.div 
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
      >
        <motion.div variants={cardVariants}>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Matches Attended</p>
                <h3 className="stat-value mt-1">{stats.matchesAttended || 0}</h3>
              </div>
              <div className="icon-container-blue">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={cardVariants}>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Stadiums Visited</p>
                <h3 className="stat-value mt-1">{stats.stadiumsVisited || 0}</h3>
              </div>
              <div className="icon-container-orange">
                <Trophy className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={cardVariants}>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Fan Since</p>
                <h3 className="stat-value mt-1">{stats.fanSince || '-'}</h3>
              </div>
              <div className="icon-container-pink">
                <Heart className="w-5 h-5 text-purple-300" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={cardVariants}>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Following</p>
                <h3 className="stat-value mt-1">{profile?.followingCount || 0}</h3>
              </div>
              <div className="icon-container-green">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="card-premium h-full">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Target className="w-4 h-4 text-primary" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0 space-y-3">
              <Button 
                className="w-full btn-primary justify-start" 
                onClick={() => navigate('/discover')}
              >
                <Users className="w-4 h-4 mr-3" />
                Discover Clubs & Athletes
              </Button>
              <Button 
                variant="outline"
                className="w-full justify-start hover-lift" 
                onClick={() => navigate('/home')}
              >
                <TrendingUp className="w-4 h-4 mr-3" />
                Latest Updates
              </Button>
              <Button 
                variant="outline"
                className="w-full justify-start hover-lift" 
                onClick={() => navigate('/messages')}
              >
                <MessageCircle className="w-4 h-4 mr-3" />
                Messages
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Favorite Teams */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="card-premium h-full">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Heart className="w-4 h-4 text-primary" />
                Favorite Teams
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              {stats.favoriteTeams && stats.favoriteTeams.length > 0 ? (
                <div className="space-y-3">
                  {stats.favoriteTeams.map((team, i) => (
                    <div 
                      key={i} 
                      className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors hover-lift"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Trophy className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-semibold text-foreground">{team.name}</p>
                            <p className="text-xs text-muted-foreground">Fan since {team.since}</p>
                          </div>
                        </div>
                        <Heart className="w-5 h-5 text-primary fill-primary" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Heart className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground mb-3">No favorite teams added yet</p>
                  <Button variant="outline" size="sm" onClick={() => navigate('/discover')}>
                    Find Teams to Follow
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Upcoming Events */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="card-premium h-full">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Calendar className="w-4 h-4 text-primary" />
                Upcoming Events
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground mb-3">No upcoming events</p>
                <Button variant="outline" size="sm" onClick={() => navigate('/trials')}>
                  Browse Events
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Fan Badges */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <Card className="card-premium h-full">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Star className="w-4 h-4 text-purple-300" />
                Fan Badges
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 rounded-xl bg-gradient-to-br from-purple-400/10 to-purple-600/10">
                  <Star className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                  <p className="text-xs font-medium text-foreground">Early Adopter</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-muted/30 opacity-50">
                  <Trophy className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">10 Matches</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-muted/30 opacity-50">
                  <Heart className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Super Fan</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default FanDashboard;
